import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BillingSyncBadge } from "@/components/BillingSyncBadge";
import { DashboardKpiCard } from "@/components/dashboard/DashboardKpiCard";
import { InvoicePrintSheet, printInvoice } from "@/components/pharmacy/InvoicePrintSheet";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import {
  loadBillingSyncState,
  syncAllInvoicesToBilling,
  type BillingSyncState,
} from "@/core/billing/billing-sync-api";
import { buildRevenueBridgeExport, downloadRevenueBridgeJson } from "@/core/billing/revenue-bridge";
import { t } from "@/core/composition/lexicon";
import { buildDashboardAnalytics } from "@/core/pharmacy/dashboard-analytics";
import { formatInvoiceDateShort, formatMoney } from "@/core/pharmacy/format";
import {
  downloadInvoicesCsv,
  getDashboardStats,
  listValidatedInvoices,
  searchValidatedInvoices,
} from "@/core/pharmacy/pharmacy-store";
import type { ValidatedInvoice } from "@/core/pharmacy/types";
import { routes } from "@/router/all_routes";

const DEFAULT_PAGE_SIZE = 25;

function IconRevenue() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconInvoice() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM14 2v6h6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSync() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8M3 3v5h5M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16M16 21h5v-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconMonth() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function PharmacyBillingPage() {
  const stats = getDashboardStats();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<ValidatedInvoice | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<BillingSyncState | null>(() => loadBillingSyncState());
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const invoices = useMemo(() => {
    void refreshKey;
    return listValidatedInvoices();
  }, [refreshKey]);

  const analytics = useMemo(() => buildDashboardAnalytics(invoices), [invoices.length]);

  const handleBillingSync = async () => {
    if (invoices.length === 0) return;
    setSyncing(true);
    setSyncMsg(null);
    try {
      const state = await syncAllInvoicesToBilling(invoices);
      setSyncState(state);
      setSyncMsg(
        `Synchronisation terminée — ${state.synced} créée(s), ${state.skipped} ignorée(s), ${state.failed} échec(s).`,
      );
      setRefreshKey((value) => value + 1);
    } catch (err) {
      setSyncMsg(err instanceof Error ? err.message : "Synchronisation billing impossible.");
    } finally {
      setSyncing(false);
    }
  };

  const filtered = useMemo(() => {
    if (query.trim()) return searchValidatedInvoices(query);
    return invoices;
  }, [query, invoices]);

  const bridge = useMemo(() => buildRevenueBridgeExport(), [invoices.length, stats.invoiceCount]);

  const unsyncedCount = useMemo(
    () =>
      invoices.filter(
        (inv) =>
          !inv.billingSync ||
          inv.billingSync.status === "pending" ||
          inv.billingSync.status === "failed",
      ).length,
    [invoices],
  );

  const filteredTotal = useMemo(
    () => filtered.reduce((sum, inv) => sum + inv.subtotal, 0),
    [filtered],
  );

  useEffect(() => {
    setPage(1);
  }, [query, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedInvoices = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  return (
    <div>
      <PageHeader
        title="Recettes & facturation"
        description="Ventes comptoir enregistrées — export et synchronisation vers le module billing."
        actions={
          <div className="d-flex gap-2 flex-wrap ph-page-header-actions">
            <Link to={routes.tenantDashboard} className="btn btn-outline-secondary btn-sm">
              Tableau de bord
            </Link>
            <Link to={routes.billingAccounting} className="btn btn-outline-secondary btn-sm">
              Comptabilité
            </Link>
            <Link to={routes.billingCashier} className="btn btn-outline-secondary btn-sm">
              Caisse
            </Link>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => void handleBillingSync()}
              disabled={invoices.length === 0 || syncing}
            >
              {syncing ? "Synchronisation…" : "Synchroniser billing"}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={downloadInvoicesCsv}
              disabled={invoices.length === 0}
            >
              Export CSV
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={downloadRevenueBridgeJson}
              disabled={invoices.length === 0}
            >
              Export JSON
            </button>
            <Link to={routes.pharmacyInvoice} className="btn btn-primary btn-sm">
              Nouvelle vente
            </Link>
          </div>
        }
      />

      <div className="row g-3 mb-4">
        <div className="col-6 col-xl-3">
          <DashboardKpiCard
            label="Recettes du jour"
            value={formatMoney(stats.revenueToday)}
            hint="Ventes comptoir validées"
            icon={<IconRevenue />}
            accent="teal"
          />
        </div>
        <div className="col-6 col-xl-3">
          <DashboardKpiCard
            label="CA du mois"
            value={formatMoney(analytics.revenueMonth)}
            hint={`${stats.invoiceCount} facture(s) au total`}
            icon={<IconMonth />}
            accent="emerald"
          />
        </div>
        <div className="col-6 col-xl-3">
          <DashboardKpiCard
            label="Factures affichées"
            value={String(filtered.length)}
            hint={
              query.trim()
                ? `Montant filtré : ${formatMoney(filteredTotal)}`
                : "Toutes les ventes comptoir"
            }
            icon={<IconInvoice />}
            accent="violet"
          />
        </div>
        <div className="col-6 col-xl-3">
          <DashboardKpiCard
            label="Sync billing en attente"
            value={String(unsyncedCount)}
            hint={unsyncedCount > 0 ? "À transmettre au module billing" : "Tout est synchronisé"}
            icon={<IconSync />}
            accent={unsyncedCount > 0 ? "amber" : "teal"}
          />
        </div>
      </div>

      <div className="alert alert-light border small mb-4">
        <strong>Consolidation :</strong> {bridge.summary.invoiceCount} facture(s) pharmacie (
        {bridge.currency}).
        {syncState && (
          <span className="ms-2">
            Dernière sync — {new Date(syncState.at).toLocaleString("fr-FR")} : {syncState.synced}{" "}
            facture(s) transmise(s).
          </span>
        )}
        <span className="d-block mt-1 text-muted">
          Graphiques de performance sur le{" "}
          <Link to={routes.tenantDashboard}>tableau de bord</Link>. Impression dédiée :{" "}
          <Link to={routes.pharmacyInvoiceHistory}>historique ventes</Link>. Module billing :{" "}
          <Link to={routes.billingAdminConsole}>{t("action.advancedModule")}</Link>.
        </span>
      </div>

      {syncMsg && <div className="alert alert-info py-2 mb-4">{syncMsg}</div>}

      <div className="ph-invoice-sheet">
        <div className="d-flex flex-wrap gap-2 align-items-end justify-content-between mb-3 ph-billing-list-head">
          <div>
            <h3 className="h6 mb-1">Factures vente comptoir</h3>
            <p className="small text-muted mb-0">
              {filtered.length} facture{filtered.length !== 1 ? "s" : ""}
              {query.trim() ? ` pour « ${query.trim()} »` : ""}
              {filtered.length > 0 && (
                <span className="ms-1">· total {formatMoney(filteredTotal)}</span>
              )}
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap w-100 w-md-auto">
            <input
              type="search"
              className="form-control form-control-sm flex-grow-1"
              style={{ minWidth: "14rem" }}
              placeholder="N°, client, produit…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center text-muted py-4">
            Aucune facture —{" "}
            <Link to={routes.pharmacyInvoice}>enregistrer une première vente</Link>.
          </div>
        ) : (
          <>
            <div className="table-responsive ph-diag-table">
              <table className="table table-sm ph-dash-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>N°</th>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Sync billing</th>
                    <th className="text-end">Montant</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedInvoices.map((inv) => (
                    <tr key={inv.id} className="ph-diag-row">
                      <td data-label="N°">
                        <span className="ph-inv-badge">{inv.number}</span>
                      </td>
                      <td data-label="Date" className="small">
                        {formatInvoiceDateShort(inv.validatedAt)}
                      </td>
                      <td data-label="Client">{inv.customer.name}</td>
                      <td data-label="Sync billing">
                        <BillingSyncBadge sync={inv.billingSync} />
                      </td>
                      <td data-label="Montant" className="text-end fw-semibold text-success">
                        {formatMoney(inv.subtotal)}
                      </td>
                      <td data-label="Actions" className="text-end">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 me-2"
                          onClick={() => setSelected(inv)}
                        >
                          Voir
                        </button>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() => printInvoice(inv)}
                        >
                          Imprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePagination
              page={safePage}
              pageSize={pageSize}
              totalItems={filtered.length}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </div>

      {selected && (
        <div
          className="ph-modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setSelected(null)}
        >
          <div
            className="ph-modal-card ph-invoice-detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-start mb-3">
              <h3 className="h5 mb-0">Facture {selected.number}</h3>
              <button
                type="button"
                className="btn-close"
                aria-label="Fermer"
                onClick={() => setSelected(null)}
              />
            </div>
            <InvoicePrintSheet invoice={selected} />
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setSelected(null)}
              >
                Fermer
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => printInvoice(selected)}
              >
                Imprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
